import { useState } from "react";
import { Eye, Pencil, Link as LinkIcon, Trash2, Loader2, Globe, X, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Link } from "react-router-dom";
import { EditNorthDialog } from "./EditNorthDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTeamUsage } from "@/hooks/useTeamUsage";
import { UpgradeDialog } from "./UpgradeDialog";
import { logger } from "@/lib/logger";
import { canCreateOrEditNorth } from "@/utils/subscriptionAccess";
import type { NorthData } from "./NorthRow";

interface NorthCardProps {
  north: NorthData;
  onUpdate?: () => void;
  hoverColor?: string;
  isAdmin?: boolean;
  actionsTourId?: string;
}

export function NorthCard({ north, onUpdate, hoverColor = "#e8edf5", isAdmin = false, actionsTourId }: NorthCardProps) {
  const { toast } = useToast();
  const { upgradeDialogOpen, setUpgradeDialogOpen, upgradeFeature, subscription } = useTeamUsage();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const fullAddress = `${north.address}, ${north.city}, ${north.state} ${north.zip}`;
  const agentName = north.listingAgent?.trim() || null;
  const agentPhoto = north.listingAgentPhoto || null;
  const agentInitials = agentName
    ? agentName.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "";
  const canEdit = subscription
    ? canCreateOrEditNorth(subscription.status, subscription.current_period_end, subscription.trial_ends_at)
    : true;

  const handleDelete = async () => {
    if (deleteConfirmText.toLowerCase() !== "delete") {
      toast({ title: "Error", description: "Please type 'delete' to confirm", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const { data: northRecord, error: fetchError } = await supabase
        .from("norths")
        .select("user_id, is_published, is_draft")
        .eq("id", north.id)
        .single();
      if (fetchError) throw fetchError;
      void northRecord;
      const { error } = await supabase.from("norths").delete().eq("id", north.id);
      if (error) throw error;
      toast({ title: "Success", description: "North deleted successfully" });
      onUpdate?.();
    } catch (error) {
      logger.error("Error deleting north:", error);
      toast({ title: "Error", description: "Failed to delete north", variant: "destructive" });
    } finally {
      setIsLoading(false);
      setIsDeleteDialogOpen(false);
      setDeleteConfirmText("");
    }
  };

  const handlePublishToggle = async () => {
    setIsPublishing(true);
    try {
      const { error } = await supabase
        .from("norths")
        .update({
          is_published: !north.isPublished,
          published_at: !north.isPublished ? new Date().toISOString() : null,
        })
        .eq("id", north.id);
      if (error) throw error;
      toast({ title: north.isPublished ? "Report Unpublished" : "Report Published" });
      onUpdate?.();
    } catch (error) {
      logger.error("Error toggling publish status:", error);
      toast({
        title: "Error",
        description: `Failed to ${north.isPublished ? "unpublish" : "publish"} report`,
        variant: "destructive",
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCopyLink = async () => {
    if (!north.isPublished) {
      toast({ title: "Report not published", description: "Publish the report first", variant: "destructive" });
      return;
    }
    const publicUrl = `${window.location.origin}/north/${north.id}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast({ title: "Link copied!", description: "Public report URL copied to clipboard" });
    } catch {
      toast({ title: "Error", description: "Failed to copy link to clipboard", variant: "destructive" });
    }
  };

  return (
    <>
      <EditNorthDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        northId={north.id}
        currentAddress={north.address}
        currentCity={north.city}
        currentState={north.state}
        currentZip={north.zip}
        currentSellerNames={north.sellerNames}
        currentSellerEmail={north.sellerEmail}
        currentListingAgent={north.listingAgent}
        currentListingAgentPhoto={north.listingAgentPhoto}
        onSuccess={onUpdate}
      />

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsDeleteDialogOpen(open);
          if (!open) setDeleteConfirmText("");
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this North Report. This action cannot be undone.
              <div className="mt-4">
                <Label htmlFor="northCardDeleteConfirm">Type "delete" to confirm:</Label>
                <Input
                  id="northCardDeleteConfirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="delete"
                  className="mt-2"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isLoading || deleteConfirmText.toLowerCase() !== "delete"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="bg-card border border-border rounded-xl overflow-hidden flex flex-col group hover:shadow-md transition-shadow duration-200">
        {/* Property image */}
        <div className="relative aspect-[16/9] overflow-hidden bg-muted flex-shrink-0">
          <img
            src={north.imageUrl}
            alt={fullAddress}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
          />
          <div className="absolute top-2.5 left-2.5">
            <span className="text-[10px] font-semibold tracking-[0.15em] uppercase bg-black/55 text-white px-2 py-1 rounded-md backdrop-blur-sm">
              North
            </span>
          </div>
          {north.isPublished && north.publicUrlViewsCount !== undefined && north.publicUrlViewsCount > 0 && (
            <div className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-black/55 text-white text-xs px-2 py-1 rounded-md backdrop-blur-sm">
              <Eye className="h-3 w-3" />
              <span>{north.publicUrlViewsCount.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Card body */}
        <div className="p-4 flex-1 flex flex-col gap-2 min-h-0">
          <Link
            to={`/seller-report/${north.id}`}
            className="font-semibold text-sm text-foreground hover:text-primary hover:underline leading-snug line-clamp-2"
          >
            {fullAddress}
          </Link>

          <div className="flex items-center gap-2 flex-wrap">
            {north.isProcessing ? (
              <Badge variant="default" className="gap-1 bg-blue-600 text-white text-xs">
                <Loader2 className="h-3 w-3 animate-spin" />
                Processing
              </Badge>
            ) : north.isPublished ? (
              <Badge variant="default" className="gap-1 bg-green-600 text-white text-xs">
                <CheckCircle2 className="h-3 w-3" />
                Published
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 text-xs">
                <XCircle className="h-3 w-3" />
                Draft
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-1.5 mt-auto pt-1.5">
            {agentPhoto ? (
              <img src={agentPhoto} alt={agentName || ""} className="h-5 w-5 rounded-full object-cover flex-shrink-0" />
            ) : agentInitials ? (
              <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <span className="text-[9px] font-semibold text-muted-foreground">{agentInitials}</span>
              </div>
            ) : null}
            {agentName && (
              <span className="text-xs text-muted-foreground truncate flex-1">{agentName}</span>
            )}
            <span className="text-xs text-muted-foreground/70 flex-shrink-0 ml-auto">{north.dateCreated}</span>
          </div>
        </div>

        {/* Action footer */}
        <div className="px-3 py-2 border-t border-border flex items-center gap-0.5" {...(actionsTourId ? { 'data-tour': actionsTourId } : {})}>
          <Link
            to={`/seller-report/${north.id}`}
            title="Preview"
            className="inline-flex items-center gap-1.5 flex-1 justify-center h-8 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverColor; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
          >
            <Eye className="h-3.5 w-3.5" />
            Preview
          </Link>
          <div className="w-px h-4 bg-border mx-0.5" />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            onClick={() => setIsEditDialogOpen(true)}
            disabled={!canEdit}
            title={!canEdit ? "Cannot edit with a canceled subscription" : "Edit"}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            onClick={handlePublishToggle}
            disabled={isPublishing}
            title={north.isPublished ? "Unpublish" : "Publish"}
          >
            {isPublishing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : north.isPublished ? (
              <Globe className="h-3.5 w-3.5 text-green-600" />
            ) : (
              <div className="relative inline-flex items-center justify-center h-3.5 w-3.5">
                <Globe className="h-3.5 w-3.5" />
                <X className="h-3.5 w-3.5 absolute rotate-45" />
              </div>
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            onClick={handleCopyLink}
            disabled={!north.isPublished}
            title={north.isPublished ? "Copy link" : "Report not published"}
          >
            <LinkIcon className="h-3.5 w-3.5" />
          </Button>
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsDeleteDialogOpen(true)}
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          )}
        </div>
      </div>

      <UpgradeDialog
        open={upgradeDialogOpen}
        onOpenChange={setUpgradeDialogOpen}
        feature={upgradeFeature}
        currentPlan={subscription?.plan_tier}
      />
    </>
  );
}
